/**
 * Shared personal-activity readiness rules used before planning and starting sessions. Required
 * fields include title, bundle, positive duration, four finite P/RJ scores, and eligible required
 * user attributes; internal review and prediction metadata are excluded.
 *
 * @module apps/web/src/lib/activityReadiness
 */

import type { AttributeDef, TaskStateProfile, TaskUI } from '@fluxability/types';

/** Partial activity shape accepted by readiness checks so incomplete editor drafts can be inspected without fabricating saved fields. */
type PlanningActivity = Partial<TaskUI> & {
  /** Optional custom-value map containing duration provenance and planning flags. */
  customAttributes?: Record<string, unknown>;
  /** Optional P/RJ planning inputs inspected for missing required values. */
  stateProfile?: Partial<TaskStateProfile> | null;
};

/** Four state dimensions needed for planning, paired with compact missing-field labels. */
const REQUIRED_STATE_FIELDS: Array<[keyof TaskStateProfile, string]> = [
  ['required_productivity_energy', 'required P'],
  ['required_rejuvenation_energy', 'required RJ'],
  ['productivity_return', 'P return'],
  ['rejuvenation_return', 'RJ return'],
];

/**
 * Return deduplicated labels for absent core planning values and required user attributes, with
 * support for historical emotion aliases.
 */
export function getMissingPlanningFields(
  activity: PlanningActivity,
  attributes: AttributeDef[] = []
): string[] {
  const missing: string[] = [];
  const customAttributes = activity.customAttributes || {};
  if (!String(activity.title || '').trim()) missing.push('title');
  if (!activity.bundleId) missing.push('bundle');
  if (!Number.isFinite(Number(activity.estimatedDuration)) || Number(activity.estimatedDuration) <= 0) {
    missing.push('duration');
  }

  for (const [key, label] of REQUIRED_STATE_FIELDS) {
    const value = activity.stateProfile?.[key];
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      missing.push(label);
    }
  }

  for (const attr of attributes) {
    if (!isUserPlanningAttribute(attr)) continue;
    const value = getPlanningAttributeValue(customAttributes, attr);
    if (value === null || value === undefined || value === '') missing.push(attr.name);
  }

  return Array.from(new Set(missing));
}

/**
 * Return whether the activity satisfies all shared core and required-attribute readiness checks.
 */
export function isActivityPlanningComplete(
  activity: PlanningActivity,
  attributes: AttributeDef[] = []
): boolean {
  return getMissingPlanningFields(activity, attributes).length === 0;
}

/**
 * Format missing-field labels for compact UI feedback, truncating the visible list when it exceeds
 * the supplied limit.
 */
export function formatMissingPlanningFields(missing: string[], limit = 5): string {
  if (missing.length === 0) return '';
  return `${missing.slice(0, limit).join(', ')}${missing.length > limit ? ` and ${missing.length - limit} more` : ''}`;
}

/**
 * Resolve an activity's custom attribute value, including legacy emotion-name aliases.
 */
export function getPlanningAttributeValue(
  customAttributes: Record<string, unknown> | undefined,
  attr: AttributeDef
): unknown {
  const custom = customAttributes || {};
  const directValue = custom[attr.attr_id];
  if (directValue !== null && directValue !== undefined && directValue !== '') return directValue;

  const normalizedName = normalizeAttributeName(attr.name);
  if (normalizedName === 'emotion') {
    return custom.emotion_word || custom.emotion || custom.emotionWord || directValue;
  }

  return directValue;
}

/**
 * Exclude internal/source/review/prediction attributes from fields that the user must supply for
 * planning.
 */
export function isUserPlanningAttribute(attr: AttributeDef): boolean {
  if (!attr.required) return false;
  const normalizedName = normalizeAttributeName(attr.name);
  return ![
    'needs review',
    'review',
    'required fields complete',
    'missing required fields',
    'duration predicted',
    'duration prediction',
    'local title override',
    'local title',
    'source',
    'external source title',
  ].includes(normalizedName);
}

/** Normalize separators, whitespace, and case before comparing required fields with the internal-metadata denylist and historical emotion aliases. */
function normalizeAttributeName(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}
